import { useState } from "react";
import {
  View,
  Text,
  TextInput,
  Pressable,
  ActivityIndicator,
  Alert,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { Ionicons } from "@expo/vector-icons";
import { useRouter } from "expo-router";
import { supabase } from "@/lib/supabase/client";
import { useAuth } from "@/providers/AuthProvider";
import { useThemeColors } from "@/providers/ThemeProvider";

/**
 * Predefined team colors
 */
const TEAM_COLORS = [
  { name: "Rood", hex: "#EF4444" },
  { name: "Oranje", hex: "#F97316" },
  { name: "Amber", hex: "#F59E0B" },
  { name: "Geel", hex: "#EAB308" },
  { name: "Groen", hex: "#22C55E" },
  { name: "Teal", hex: "#14B8A6" },
  { name: "Blauw", hex: "#3B82F6" },
  { name: "Indigo", hex: "#6366F1" },
  { name: "Paars", hex: "#8B5CF6" },
  { name: "Roze", hex: "#EC4899" },
  { name: "Grijs", hex: "#6B7280" },
  { name: "Zwart", hex: "#1F2937" },
];

/**
 * Create Team Screen
 */
export default function CreateTeamScreen() {
  const router = useRouter();
  const { activeWeekend, user } = useAuth();
  const colors = useThemeColors();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [selectedColor, setSelectedColor] = useState(TEAM_COLORS[0].hex);
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Handle create team
   */
  const handleCreate = async () => {
    if (!activeWeekend || !user) {
      Alert.alert("Fout", "Geen actief weekend gevonden");
      return;
    }

    const trimmedName = name.trim();
    if (!trimmedName) {
      Alert.alert("Fout", "Vul een teamnaam in");
      return;
    }

    // Confirmation
    Alert.alert(
      "Team Aanmaken",
      `Weet je zeker dat je "${trimmedName}" wilt aanmaken?`,
      [
        { text: "Annuleer", style: "cancel" },
        {
          text: "Aanmaken",
          onPress: async () => {
            setIsLoading(true);

            try {
              const { error } = await supabase.from("teams").insert({
                weekend_id: activeWeekend.id,
                name: trimmedName,
                description: description.trim() || null,
                color: selectedColor,
                created_by: user.id,
              });

              if (error) throw error;

              Alert.alert("Succes! 🎉", `Team "${trimmedName}" is aangemaakt`, [
                { text: "OK", onPress: () => router.back() },
              ]);
            } catch (error: any) {
              console.error("Error creating team:", error);
              Alert.alert(
                "Fout",
                error.message || "Kon team niet aanmaken"
              );
            } finally {
              setIsLoading(false);
            }
          },
        },
      ]
    );
  };

  if (!activeWeekend) {
    return (
      <SafeAreaView
        className="flex-1 items-center justify-center"
        style={{ backgroundColor: colors.background }}
        edges={["bottom"]}
      >
        <Ionicons name="calendar-outline" size={48} color="#D97706" />
        <Text className="mt-2" style={{ color: "#D97706" }}>
          Geen actief weekend
        </Text>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView
      className="flex-1"
      style={{ backgroundColor: colors.background }}
      edges={["bottom"]}
    >
      <KeyboardAvoidingView
        behavior={Platform.OS === "ios" ? "padding" : "height"}
        className="flex-1"
        keyboardVerticalOffset={Platform.OS === "ios" ? 90 : 0}
      >
        <ScrollView
          className="flex-1 px-4"
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
          contentContainerStyle={{ paddingBottom: 40 }}
        >
          {/* Preview */}
          <View className="items-center py-6">
            <View
              className="w-24 h-24 rounded-full items-center justify-center mb-3"
              style={{ backgroundColor: `${selectedColor}20` }}
            >
              <Text
                className="text-4xl font-bold"
                style={{ color: selectedColor }}
              >
                {name.trim().charAt(0).toUpperCase() || "T"}
              </Text>
            </View>
            <Text
              className="text-xl font-bold"
              style={{ color: selectedColor }}
            >
              {name.trim() || "Team Naam"}
            </Text>
            {description.trim() && (
              <Text className="italic mt-1" style={{ color: colors.textSecondary }}>
                "{description.trim()}"
              </Text>
            )}
          </View>

          {/* Form */}
          <View
            className="rounded-2xl p-4 mb-4"
            style={{
              backgroundColor: colors.surface,
              borderWidth: 1,
              borderColor: colors.border,
            }}
          >
            {/* Team Name */}
            <View className="mb-4">
              <Text className="text-sm font-medium mb-2" style={{ color: colors.text }}>
                Team Naam <Text style={{ color: "#EF4444" }}>*</Text>
              </Text>
              <TextInput
                className="rounded-xl px-4 py-3"
                style={{
                  backgroundColor: colors.background,
                  borderWidth: 1,
                  borderColor: colors.border,
                  color: colors.text,
                }}
                placeholder="bijv. Team Rood"
                placeholderTextColor={colors.textTertiary}
                value={name}
                onChangeText={setName}
                maxLength={30}
                autoCapitalize="words"
              />
              <Text className="text-xs mt-1 text-right" style={{ color: colors.textTertiary }}>
                {name.length}/30
              </Text>
            </View>

            {/* Team Slogan */}
            <View className="mb-4">
              <Text className="text-sm font-medium mb-2" style={{ color: colors.text }}>
                Slogan / Motto (optioneel)
              </Text>
              <TextInput
                className="rounded-xl px-4 py-3"
                style={{
                  backgroundColor: colors.background,
                  borderWidth: 1,
                  borderColor: colors.border,
                  color: colors.text,
                }}
                placeholder="bijv. Wij winnen altijd!"
                placeholderTextColor={colors.textTertiary}
                value={description}
                onChangeText={setDescription}
                maxLength={50}
              />
              <Text className="text-xs mt-1 text-right" style={{ color: colors.textTertiary }}>
                {description.length}/50
              </Text>
            </View>

            {/* Team Color */}
            <View>
              <Text className="text-sm font-medium mb-3" style={{ color: colors.text }}>
                Team Kleur
              </Text>
              <View className="flex-row flex-wrap">
                {TEAM_COLORS.map((color) => (
                  <Pressable
                    key={color.hex}
                    onPress={() => setSelectedColor(color.hex)}
                    className="mr-2 mb-2"
                    accessibilityRole="button"
                    accessibilityLabel={`Selecteer ${color.name}`}
                  >
                    <View
                      className="w-12 h-12 rounded-xl items-center justify-center"
                      style={{
                        backgroundColor: color.hex,
                        borderWidth: selectedColor === color.hex ? 2 : 0,
                        borderColor: colors.text,
                      }}
                    >
                      {selectedColor === color.hex && (
                        <Ionicons name="checkmark" size={24} color="#FFFFFF" />
                      )}
                    </View>
                  </Pressable>
                ))}
              </View>
            </View>
          </View>

          {/* Info */}
          <View
            className="rounded-xl p-4 mb-4 flex-row"
            style={{ backgroundColor: "#FEF3C7" }}
          >
            <Ionicons name="information-circle" size={20} color="#D97706" />
            <Text className="text-sm ml-2 flex-1" style={{ color: "#92400E" }}>
              Na het aanmaken kun je leden toevoegen aan dit team via de "Leden"
              knop op het teamoverzicht.
            </Text>
          </View>
        </ScrollView>

        {/* Submit Button */}
        <View className="px-4 pb-4" style={{ backgroundColor: colors.background }}>
          <Pressable
            onPress={handleCreate}
            disabled={isLoading || !name.trim()}
            className="rounded-xl py-4 items-center"
            style={{
              backgroundColor: isLoading || !name.trim() ? colors.border : "#F59E0B",
            }}
            accessibilityRole="button"
            accessibilityLabel="Team aanmaken"
          >
            {isLoading ? (
              <ActivityIndicator color="#FFFFFF" />
            ) : (
              <Text className="text-white font-semibold text-lg">
                Team Aanmaken
              </Text>
            )}
          </Pressable>
        </View>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}
